import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PhotoIcon from '@mui/icons-material/Photo';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Helmet } from 'react-helmet-async';
import { Sponsor } from '../types';

interface LevelingItem {
  name: string;
  level: string;
  photoUrl?: string;   // kolom M (foto pemain) / D (leveling_glory)
  videoUrl?: string;   // kolom N (link YouTube) / E (leveling_glory)
  komunitas?: string;  // RaketerS atau Glory
  levelTurnamen?: string; // kolom P (Level Turnamen) - hanya untuk RaketerS
}

const Leveling: React.FC = () => {
  const [data, setData] = useState<LevelingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [levelUmumFilter, setLevelUmumFilter] = useState<string>('');
  const [komunitasFilter, setKomunitasFilter] = useState<string>('');
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const theme = useTheme();
  const mode = theme.palette.mode;

  // Popover state for hover preview
  const [previewRect, setPreviewRect] = useState<DOMRect | null>(null);
  const [popoverContent, setPopoverContent] = useState<{ type: 'photo' | 'video' | null; src?: string }>({ type: null });
  const [modalContent, setModalContent] = useState<{ type: 'photo' | 'video' | null; src?: string }>({ type: null });
  const closeTimer = React.useRef<number | null>(null);

  const openPreview = (event: React.MouseEvent<HTMLElement>, type: 'photo' | 'video', src?: string) => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    const target = event.currentTarget as HTMLElement;
    setPreviewRect(target.getBoundingClientRect());
    setPopoverContent({ type, src });
  };

  const openModal = (type: 'photo' | 'video', src?: string) => {
    setModalContent({ type, src });
  };

  const closeModal = () => {
    setModalContent({ type: null });
  };

  const scheduleClosePreview = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    // delay removal so moving pointer doesn't close immediately
    closeTimer.current = window.setTimeout(() => {
      setPreviewRect(null);
      setPopoverContent({ type: null });
      closeTimer.current = null;
    }, 400) as unknown as number;
  };

  // Helper function to convert Level Lokal to Level Bandung/Umum
  const getLevelBandung = (levelLokal: string, komunitas: string): string => {
    if (!levelLokal || !komunitas) return '-';

    if (komunitas === 'RaketerS') {
      // RaketerS mapping
      const normalized = levelLokal.trim().toLowerCase();
      
      if (normalized === 'advanced' || normalized === 'advanced ') return 'E+';
      if (normalized === 'advanced -' || normalized === 'advanced-') return 'E';
      if (normalized === 'intermediate' || normalized === 'intermediate -' || normalized === 'intermediate-' || normalized === 'intermediate +' || normalized === 'intermediate+') return 'F';
      if (normalized === 'advanced +' || normalized === 'advanced+') return 'D';
      if (normalized === 'beginner' || normalized === 'beginner ') return 'G';
      
      return '-';
    } else if (komunitas === 'Glory') {
      // Glory mapping
      const normalized = levelLokal.trim().replace(/\s+/g, '').toUpperCase();
      
      if (normalized === 'A+') return 'D+';
      if (normalized === 'A' || normalized === 'A-') return 'E+/D';
      if (normalized === 'B' || normalized === 'B+' || normalized === 'B-') return 'E-/E';
      if (normalized === 'C' || normalized === 'C+' || normalized === 'C-') return 'F+/E-';
      if (normalized === 'D') return 'F';
      if (normalized === 'E') return 'F';
      if (normalized === 'F') return 'F';
      
      return '-';
    }
    
    return '-';
  };

  const uniqueLevels = useMemo(() => {
    const levels = Array.from(new Set(data.map((item) => item.level).filter(Boolean)));
    const groupOrder = (level: string): number => {
      const lower = level.toLowerCase();
      if (lower.startsWith('beginner')) return 0;
      if (lower.startsWith('intermediate')) return 1;
      if (lower.startsWith('advanced')) return 2;
      return 3;
    };
    // Within group: - first, then neutral, then +
    const suffixOrder = (level: string): number => {
      const trimmed = level.trim();
      if (trimmed.endsWith('-')) return 0;
      if (trimmed.endsWith('+')) return 2;
      return 1; // neutral (no suffix)
    };
    return levels.sort((a, b) => {
      const ga = groupOrder(a);
      const gb = groupOrder(b);
      if (ga !== gb) return ga - gb;
      return suffixOrder(a) - suffixOrder(b);
    });
  }, [data]);

  const uniqueUmumLevels = useMemo(() => {
    const levels = Array.from(
      new Set(
        data
          .map((item) => getLevelBandung(item.level, item.komunitas || ''))
          .filter((value) => value && value !== '-')
      )
    );
    return levels.sort((a, b) => a.localeCompare(b, 'id'));
  }, [data]);

  const filteredData = useMemo(() => {
    let result = data;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((item) => item.name.toLowerCase().includes(q));
    }
    if (levelFilter) {
      result = result.filter((item) => item.level === levelFilter);
    }
    if (levelUmumFilter) {
      result = result.filter((item) => getLevelBandung(item.level, item.komunitas || '') === levelUmumFilter);
    }
    if (komunitasFilter) {
      result = result.filter((item) => item.komunitas === komunitasFilter);
    }
    return [...result].sort((a, b) => a.name.localeCompare(b.name, 'id'));
  }, [data, searchQuery, levelFilter, levelUmumFilter, komunitasFilter]);

  useEffect(() => {
    const fetchLeveling = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/bookings?action=leveling');
        if (!response.ok) throw new Error('Gagal mengambil data leveling');
        const result = await response.json();
        setData(result.data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Terjadi kesalahan');
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLeveling();
  }, []);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await axios.get<Sponsor[]>('/api/bookings?action=sponsor');
        setSponsors(response.data);
      } catch (err) {
        console.error('Error fetching sponsors:', err);
        setSponsors([]);
      }
    };
    fetchSponsors();
  }, []);

  const SponsorSidebar: React.FC<{ sponsors: Sponsor[]; position: 'left' | 'right' }> = ({ sponsors: sp, position }) => {
    if (sp.length === 0) return null;
    return (
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          gap: 3,
          alignItems: 'center',
          justifyContent: 'flex-start',
          position: 'fixed',
          ...(position === 'left' ? { left: { xs: 0, lg: 20 } } : { right: { xs: 0, lg: 20 } }),
          top: 100,
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
          width: 180,
          zIndex: 100,
          px: 2,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: mode === 'dark' ? 'rgba(99,170,255,0.3)' : 'rgba(25,118,210,0.3)',
            borderRadius: '3px',
          },
        }}
      >
        {sp.map((sponsor, index) => (
          <Box
            key={sponsor.id}
            component="img"
            src={sponsor.url}
            alt={`Sponsor ${index + 1}`}
            sx={{
              height: 'auto',
              width: '100%',
              maxWidth: 150,
              objectFit: 'contain',
              boxShadow: mode === 'light'
                ? '0 8px 32px rgba(25,118,210,0.15)'
                : '0 8px 32px rgba(13,71,161,0.3)',
              borderRadius: 3,
              background: mode === 'light'
                ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                : 'linear-gradient(135deg, rgba(13,71,161,0.1) 0%, rgba(21,101,192,0.05) 100%)',
              backdropFilter: 'blur(10px)',
              border: mode === 'light'
                ? '1px solid rgba(255,255,255,0.3)'
                : '1px solid rgba(255,255,255,0.1)',
              p: 1.5,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: mode === 'light'
                  ? '0 12px 40px rgba(25,118,210,0.2)'
                  : '0 12px 40px rgba(13,71,161,0.4)',
              },
            }}
          />
        ))}
      </Box>
    );
  };

  // helper to derive youtube embed link from provided url/id
  const getYoutubeEmbed = (url: string) => {
    if (!url) return '';
    // attempt to extract video ID
    const regex = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/;
    const match = url.match(regex);
    const id = match ? match[1] : url;
    return `https://www.youtube.com/embed/${id}`;
  };

  return (
    <Box sx={{ width: '100%', py: 4, position: 'relative' }}>
      <SponsorSidebar sponsors={sponsors} position="left" />
      <SponsorSidebar sponsors={sponsors} position="right" />
      <Box sx={{ maxWidth: 950, mx: 'auto', py: 4, px: 2 }}>
      <Helmet>
        <title>Leveling - RaketerS</title>
      </Helmet>
      <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
        Leveling Pemain
      </Typography>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Data level pemain (Di-update 8 April 2026)
        </Typography>
      </Box>

      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Collaboration with:
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap',
            mt: 2,
          }}
        >
          <Box
            component="img"
            src="/images/collab/glory.png"
            alt="Glory Community"
            sx={{
              height: 'auto',
              width: 'auto',
              maxWidth: 120,
              maxHeight: 80,
              objectFit: 'contain',
              filter: mode === 'dark' ? 'brightness(0.9)' : 'brightness(1)',
              opacity: 0.85,
              transition: 'all 0.3s ease',
              '&:hover': {
                opacity: 1,
                transform: 'scale(1.05)',
              },
            }}
          />
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 120px 120px 120px',
              },
              gap: 2,
              mb: 2,
              width: '100%',
              maxWidth: 900,
              mx: 'auto',
            }}
          >
            <TextField
              placeholder="Cari nama pemain..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ width: '100%', minWidth: 160, maxWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 140, width: '100%' }}>
              <InputLabel id="level-filter-label">Level Lokal</InputLabel>
              <Select
                labelId="level-filter-label"
                value={levelFilter}
                label="Level Lokal"
                onChange={(e) => setLevelFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>Semua Level Lokal</em>
                </MenuItem>
                {uniqueLevels.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140, width: '100%' }}>
              <InputLabel id="level-umum-filter-label">Level Umum</InputLabel>
              <Select
                labelId="level-umum-filter-label"
                value={levelUmumFilter}
                label="Level Umum"
                onChange={(e) => setLevelUmumFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>Semua Level Umum</em>
                </MenuItem>
                {uniqueUmumLevels.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140, width: '100%' }}>
              <InputLabel id="komunitas-filter-label">Komunitas</InputLabel>
              <Select
                labelId="komunitas-filter-label"
                value={komunitasFilter}
                label="Komunitas"
                onChange={(e) => setKomunitasFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>Semua Komunitas</em>
                </MenuItem>
                <MenuItem value="RaketerS">RaketerS</MenuItem>
                <MenuItem value="Glory">Glory</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Paper
            sx={{
              p: 0,
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)'
                : undefined,
              border: theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined,
              borderRadius: 3,
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      borderBottom:
                        theme.palette.mode === 'dark'
                          ? '2px solid rgba(99,170,255,0.35)'
                          : '2px solid rgba(0,0,0,0.1)',
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700, width: 60 }}>No.</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 180 }}>Nama</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 140 }}>Level Lokal</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 110 }}>Level Turnamen</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 110 }}>Level Umum</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 100 }}>Komunitas</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 80 }}>Foto</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 80 }}>Video</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        {data.length === 0
                          ? 'Belum ada data leveling'
                          : 'Tidak ada pemain yang cocok dengan filter'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredData.map((item, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ width: 60 }}>{idx + 1}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.level}</TableCell>
                      <TableCell>
                        {item.komunitas === 'RaketerS' ? (
                          <Box
                            sx={{
                              fontWeight: 600,
                              color: item.levelTurnamen ? 'warning.main' : 'text.secondary',
                              fontSize: '0.85rem'
                            }}
                          >
                            {item.levelTurnamen || '-'}
                          </Box>
                        ) : (
                          <Box sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>-</Box>
                        )}
                      </TableCell>
                      <TableCell>{getLevelBandung(item.level, item.komunitas || '')}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            backgroundColor: item.komunitas === 'RaketerS'
                              ? (mode === 'dark' ? 'rgba(66, 165, 245, 0.15)' : 'rgba(25, 118, 210, 0.1)')
                              : (mode === 'dark' ? 'rgba(244, 67, 54, 0.15)' : 'rgba(229, 57, 53, 0.1)'),
                            color: item.komunitas === 'RaketerS'
                              ? (mode === 'dark' ? '#42a5f5' : '#1976d2')
                              : (mode === 'dark' ? '#f44336' : '#e53935'),
                          }}
                        >
                          {item.komunitas || '-'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {item.photoUrl ? (
                          <Box
                            onMouseEnter={(e: React.MouseEvent<HTMLElement>) => openPreview(e, 'photo', item.photoUrl)}
                            onMouseLeave={scheduleClosePreview}
                            onClick={() => openModal('photo', item.photoUrl)}
                          >
                            <IconButton size="small" aria-label="lihat foto" onMouseDown={(e)=>e.preventDefault()}>
                              <PhotoIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {item.videoUrl ? (
                          <Box
                            onMouseEnter={(e: React.MouseEvent<HTMLElement>) => openPreview(e, 'video', item.videoUrl)}
                            onMouseLeave={scheduleClosePreview}
                            onClick={() => openModal('video', item.videoUrl)}
                          >
                            <IconButton size="small" aria-label="lihat video" onMouseDown={(e)=>e.preventDefault()}>
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        {/* custom preview box fixed in viewport */}
        {previewRect && popoverContent.type && (
          <Box
            onMouseEnter={() => { if (closeTimer.current) { window.clearTimeout(closeTimer.current); closeTimer.current = null; } }}
            onMouseLeave={scheduleClosePreview}
            sx={{
              position: 'fixed',
              top: previewRect.bottom + 4,
              left: previewRect.left,
              zIndex: 1300,
              bgcolor: 'background.paper',
              p: 1,
              boxShadow: 1,
              maxWidth: 560,
              pointerEvents: 'auto'
            }}
          >
            {popoverContent.type === 'photo' && popoverContent.src && (
              <Box component="img" src={popoverContent.src} alt="preview" sx={{ maxWidth: 520, maxHeight: 360, objectFit: 'contain' }} />
            )}
            {popoverContent.type === 'video' && popoverContent.src && (
              <Box component="iframe" src={getYoutubeEmbed(popoverContent.src || '')} width={520} height={292} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            )}
          </Box>
        )}
        {/* modal overlay for mobile clicks */}
        {modalContent.type && (
          <Box
            onClick={closeModal}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              bgcolor: 'rgba(0,0,0,0.6)',
              zIndex: 1400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box onClick={(e) => e.stopPropagation()} sx={{ maxWidth: '90%', maxHeight: '90%' }}>
              {modalContent.type === 'photo' && modalContent.src && (
                <Box component="img" src={modalContent.src} alt="preview" sx={{ width: '100%', height: 'auto', objectFit: 'contain' }} />
              )}
              {modalContent.type === 'video' && modalContent.src && (
                <Box component="iframe" src={getYoutubeEmbed(modalContent.src || '')} width="100%" height="auto" sx={{ aspectRatio: '16/9' }} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              )}
            </Box>
          </Box>
        )}
        </>
      )}
      </Box>
    </Box>
  );
};

export default Leveling;
